import { Button, useToast } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { FiBookmark } from "react-icons/fi";
import { type SavedReportDto, SavedReportListDto, useCreateSavedReportMutation } from "../../autogen/bff-api";
import { useApiError } from "../errors/useApiError";
import { Tooltip } from "../components/Tooltip";
import { type FirmReportChoice, ReportNameModal } from "./ReportNameModal";
import { reportSaveBlockedReason } from "./reportSaveBlockedReason";
import { savedReportListLabel } from "./savedReportLists";

export const SaveAsReportButton = ({
  list,
  query,
  listFailedToLoad,
  listIsLoading,
  canSaveFirmReports,
  reportsLoading,
  reportsFailed,
  onRetryReports,
  isNaming,
  onNamingChange: setIsNaming,
  onSaved,
}: {
  list: SavedReportListDto;
  query: string;
  listFailedToLoad: boolean;
  listIsLoading: boolean;
  canSaveFirmReports: boolean;
  reportsLoading: boolean;
  reportsFailed: boolean;
  onRetryReports: () => void;
  isNaming: boolean;
  onNamingChange: (isNaming: boolean) => void;
  onSaved: (report: SavedReportDto) => void;
}) => {
  const { t } = useTranslation();
  const toast = useToast();
  const { trigger } = useApiError();
  const [createSavedReport, { isLoading }] = useCreateSavedReportMutation();
  const disabledReason = reportSaveBlockedReason(t, query, listFailedToLoad);
  const saveDisabledReason =
    disabledReason ??
    (listIsLoading
      ? t("Waiting for the list to load before it can be saved.")
      : reportsLoading
        ? t("Waiting for the reports to load …")
        : undefined);
  const firmReportChoice: FirmReportChoice | undefined = reportsFailed
    ? { listName: savedReportListLabel(t, list), status: "failed", onRetry: onRetryReports }
    : reportsLoading
      ? { listName: savedReportListLabel(t, list), status: "loading" }
      : canSaveFirmReports
        ? { listName: savedReportListLabel(t, list), status: "ready" }
        : undefined;

  const save = async (name: string, firmReport: boolean) => {
    if (saveDisabledReason) return;
    try {
      const saved = await createSavedReport({ createSavedReportRequest: { name, list, query, firmReport } }).unwrap();
      toast({
        title: firmReport
          ? t("The firm report is saved for everyone in the organisation")
          : t("The report is saved in My reports"),
        status: "success",
      });
      setIsNaming(false);
      onSaved(saved);
    } catch (e) {
      trigger(e);
    }
  };

  return (
    <>
      <Tooltip label={disabledReason} shouldWrapChildren>
        <Button
          size="sm"
          variant="outline"
          leftIcon={<FiBookmark />}
          onClick={() => setIsNaming(true)}
          isDisabled={!!disabledReason}
        >
          {t("Save as report")}
        </Button>
      </Tooltip>
      {isNaming && (
        <ReportNameModal
          title={String(t("Save as report"))}
          description={String(
            t(
              "The report keeps the filter, the columns and the sorting. It opens with the data as it is then. Only you see your reports, unless you share them."
            )
          )}
          confirmLabel={String(t("Save"))}
          isLoading={isLoading}
          confirmDisabledReason={saveDisabledReason ? String(saveDisabledReason) : undefined}
          firmReportChoice={firmReportChoice}
          onConfirm={(name, firmReport) => void save(name, firmReport)}
          onClose={() => setIsNaming(false)}
        />
      )}
    </>
  );
};
